/**
 * Email Header Parser - Analyzes raw email headers
 */

export interface BasicInfo {
  subject: string;
  from: string;
  to: string;
  date: string;
  messageId: string;
  returnPath: string;
}

export interface AuthResult {
  status: 'pass' | 'fail' | 'none' | 'unknown';
  details: string;
  domain?: string;
}

export interface DmarcResult extends AuthResult {
  policy?: string;
  alignment?: string;
}

export interface ArcResult {
  instance: number;
  status: string;
  details: string;
}

export interface RelayHop {
  hop: number;
  from: string;
  fromIp?: string;
  by: string;
  byIp?: string;
  protocol?: string;
  timestamp?: string;
  delay?: number;
  
  // Security fields
  tls?: {
    version: string;
    cipher: string;
    keySize?: string;
  };
  certificate?: {
    clientCN?: string;
    issuer?: string;
    verificationStatus?: string;
  };
  
  // Metadata fields
  queueId?: string;
  forAddress?: string;
  via?: string;
  
  // Suspicious indicators
  warnings?: string[];
  suspiciousLevel?: 'none' | 'low' | 'medium' | 'high';
}

export interface ParsedEmailHeaders {
  basicInfo: BasicInfo;
  authentication: {
    spf: AuthResult;
    dkim: AuthResult;
    dmarc: DmarcResult;
    arc: ArcResult[];
  };
  relayPath: RelayHop[];
  timing: {
    totalDelay: number;
    firstReceived?: Date;
    lastReceived?: Date;
  };
  headers: Map<string, string[]>;
  securityIssues: string[];
}

/**
 * Parse raw email headers into structured data
 */
export const parseEmailHeaders = (rawHeaders: string): ParsedEmailHeaders => {
  const headers = parseHeaderLines(rawHeaders);
  
  const basicInfo = extractBasicInfo(headers);
  const authentication = parseAuthenticationResults(headers);
  const relayPath = parseReceivedHeaders(headers);
  const timing = calculateDeliveryTiming(relayPath);
  const securityIssues = detectSecurityIssues(headers, authentication);

  return {
    basicInfo,
    authentication,
    relayPath,
    timing,
    headers,
    securityIssues,
  };
};

/**
 * Parse raw header text into a Map of header names to values
 */
const parseHeaderLines = (rawHeaders: string): Map<string, string[]> => {
  const headers = new Map<string, string[]>();
  const lines = rawHeaders.split('\n');
  let currentHeader = '';
  let currentValue = '';

  const saveHeader = () => {
    if (currentHeader && currentValue !== undefined) {
      const existing = headers.get(currentHeader) || [];
      headers.set(currentHeader, [...existing, currentValue.trim()]);
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Skip empty lines
    if (line.trim() === '') {
      continue;
    }
    
    // Check if this is a continuation line (starts with whitespace or tab)
    if ((line.match(/^[\s\t]/) || line.startsWith(' ')) && currentHeader) {
      currentValue += ' ' + line.trim();
    } else if (line.includes(':')) {
      // Save previous header if exists
      saveHeader();
      
      // Parse new header
      const colonIndex = line.indexOf(':');
      currentHeader = line.substring(0, colonIndex).trim().toLowerCase();
      currentValue = line.substring(colonIndex + 1).trim();
    } else if (currentHeader) {
      // Continuation line without leading whitespace (some mailers do this)
      currentValue += ' ' + line.trim();
    }
  }

  // Save last header
  saveHeader();

  return headers;
};

/**
 * Extract basic email information
 */
const extractBasicInfo = (headers: Map<string, string[]>): BasicInfo => {
  const getFirst = (key: string): string => {
    const values = headers.get(key);
    return values && values.length > 0 ? values[0] : '';
  };

  // Decode encoded headers
  const decodeHeader = (value: string): string => {
    // Handle =?charset?encoding?text?= format
    return value.replace(/=\?([^?]+)\?([QB])\?([^?]+)\?=/gi, (_match, _charset, encoding, text) => {
      if (encoding.toUpperCase() === 'Q') {
        // Quoted-printable
        return text.replace(/_/g, ' ').replace(/=([0-9A-F]{2})/gi, (_m: string, hex: string) => {
          return String.fromCharCode(parseInt(hex, 16));
        });
      }
      return text;
    });
  };

  return {
    subject: decodeHeader(getFirst('subject')),
    from: getFirst('from'),
    to: getFirst('to'),
    date: getFirst('date'),
    messageId: getFirst('message-id'),
    returnPath: getFirst('return-path'),
  };
};

/**
 * Parse authentication results (SPF, DKIM, DMARC)
 */
const parseAuthenticationResults = (headers: Map<string, string[]>): ParsedEmailHeaders['authentication'] => {
  const authResults = headers.get('authentication-results') || [];
  const receivedSpf = headers.get('received-spf') || [];
  const dkimSignature = headers.get('dkim-signature') || [];
  
  const spf: AuthResult = { status: 'none', details: 'No SPF information found' };
  const dkim: AuthResult = { status: 'none', details: 'No DKIM information found' };
  const dmarc: DmarcResult = { status: 'none', details: 'No DMARC information found' };
  const arc: ArcResult[] = [];

  // Parse Authentication-Results header
  authResults.forEach(result => {
    const lowerResult = result.toLowerCase();
    
    // SPF
    if (lowerResult.includes('spf=')) {
      const spfMatch = result.match(/spf=(\w+)/i);
      if (spfMatch) {
        spf.status = spfMatch[1].toLowerCase() as AuthResult['status'];
        spf.details = result;
        
        const domainMatch = result.match(/smtp\.mailfrom=([^\s;]+)/i);
        if (domainMatch) {
          spf.domain = domainMatch[1];
        }
      }
    }
    
    // DKIM
    if (lowerResult.includes('dkim=')) {
      const dkimMatch = result.match(/dkim=(\w+)/i);
      if (dkimMatch) {
        dkim.status = dkimMatch[1].toLowerCase() as AuthResult['status'];
        dkim.details = result;
        
        const domainMatch = result.match(/header\.d=([^\s;]+)/i);
        if (domainMatch) {
          dkim.domain = domainMatch[1];
        }
      }
    }
    
    // DMARC
    if (lowerResult.includes('dmarc=')) {
      const dmarcMatch = result.match(/dmarc=(\w+)/i);
      if (dmarcMatch) {
        dmarc.status = dmarcMatch[1].toLowerCase() as AuthResult['status'];
        dmarc.details = result;
        
        const policyMatch = result.match(/action=(\w+)/i);
        if (policyMatch) {
          dmarc.policy = policyMatch[1];
        }
      }
    }
  });

  // Parse Received-SPF header
  if (receivedSpf.length > 0 && spf.status === 'none') {
    const spfResult = receivedSpf[0];
    const statusMatch = spfResult.match(/^(\w+)/i);
    if (statusMatch) {
      spf.status = statusMatch[1].toLowerCase() as AuthResult['status'];
      spf.details = spfResult;
    }
  }

  // Check for DKIM signature
  if (dkimSignature.length > 0 && dkim.status === 'none') {
    dkim.status = 'unknown';
    dkim.details = 'DKIM signature present (verification status unknown)';
  }

  // Parse ARC headers
  const arcSeals = headers.get('arc-seal') || [];
  arcSeals.forEach((seal) => {
    const instanceMatch = seal.match(/i=(\d+)/);
    const statusMatch = seal.match(/cv=(\w+)/);
    
    if (instanceMatch) {
      arc.push({
        instance: parseInt(instanceMatch[1]),
        status: statusMatch ? statusMatch[1] : 'unknown',
        details: seal,
      });
    }
  });

  return { spf, dkim, dmarc, arc };
};

/**
 * Extract TLS information from received header
 */
const extractTLSInfo = (received: string): RelayHop['tls'] | undefined => {
  // Pattern: (using TLSv1.3 with cipher TLS_AES_256_GCM_SHA384 (256 bits))
  const tlsMatch = received.match(/using\s+(TLS[^\s]+)\s+with\s+cipher\s+([^\s(]+)(?:\s+\((\d+)\s+bits\))?/i);
  if (tlsMatch) {
    return {
      version: tlsMatch[1],
      cipher: tlsMatch[2],
      keySize: tlsMatch[3] ? `${tlsMatch[3]} bits` : undefined,
    };
  }

  // Pattern: (version=TLS1_3, cipher=TLS_AES_256_GCM_SHA384)
  const tlsMatch2 = received.match(/version=(TLS[^\s,)]+).*?cipher=([^\s,)]+)/i);
  if (tlsMatch2) {
    return {
      version: tlsMatch2[1].replace(/_/g, '.'),
      cipher: tlsMatch2[2],
    };
  }

  return undefined;
};

/**
 * Extract certificate information from received header
 */
const extractCertificateInfo = (received: string): RelayHop['certificate'] | undefined => {
  // Pattern: (Client CN "ems", Issuer "mxvalidator" (verified OK))
  const certMatch = received.match(/Client\s+CN\s+"([^"]+)".*?Issuer\s+"([^"]+)"(?:\s+\(([^)]+)\))?/i);
  if (certMatch) {
    return {
      clientCN: certMatch[1],
      issuer: certMatch[2],
      verificationStatus: certMatch[3] || undefined,
    };
  }

  return undefined;
};

/**
 * Extract queue/message ID from received header
 */
const extractQueueId = (received: string): string | undefined => {
  // Pattern: id ABC123 or id 6D1691600085
  const idMatch = received.match(/\bid\s+([^\s;]+)/i);
  return idMatch ? idMatch[1] : undefined;
};

/**
 * Extract recipient "for" address from received header
 */
const extractForAddress = (received: string): string | undefined => {
  // Pattern: for <email@domain.com>
  const forMatch = received.match(/for\s+<([^>]+)>/i);
  return forMatch ? forMatch[1] : undefined;
};

/**
 * Extract "via" information from received header
 */
const extractViaInfo = (received: string): string | undefined => {
  // Pattern: via Frontend Transport
  const viaMatch = received.match(/via\s+([^;]+?)(?:;|\s+\w+:)/i);
  return viaMatch ? viaMatch[1].trim() : undefined;
};

/**
 * Evaluate cipher strength
 */
const getCipherStrength = (cipher: string): 'strong' | 'weak' | 'deprecated' => {
  const cipherLower = cipher.toLowerCase();
  
  // Deprecated/weak ciphers
  if (cipherLower.includes('rc4') || cipherLower.includes('des') || cipherLower.includes('md5')) {
    return 'deprecated';
  }
  
  // Weak ciphers
  if (cipherLower.includes('3des') || cipherLower.includes('cbc')) {
    return 'weak';
  }
  
  // Strong ciphers (GCM, AES-256, ChaCha20)
  if (cipherLower.includes('gcm') || cipherLower.includes('aes_256') || cipherLower.includes('chacha20')) {
    return 'strong';
  }
  
  return 'weak';
};

/**
 * Check if delay is suspicious
 */
const isSuspiciousDelay = (delay: number | undefined): boolean => {
  if (delay === undefined) return false;
  // Negative delays (time travel) or very long delays (>5 minutes)
  return delay < 0 || delay > 300;
};

/**
 * Check if IP is private/local
 */
const isPrivateIP = (ip: string | undefined): boolean => {
  if (!ip) return false;
  
  // Check for private IPv4 ranges
  const privateRanges = [
    /^10\./,
    /^172\.(1[6-9]|2[0-9]|3[01])\./,
    /^192\.168\./,
    /^127\./,
    /^169\.254\./,
  ];
  
  return privateRanges.some(range => range.test(ip));
};

/**
 * Detect suspicious patterns in a relay hop
 */
const detectSuspiciousHop = (hop: RelayHop, received: string): void => {
  const warnings: string[] = [];
  let suspiciousLevel: RelayHop['suspiciousLevel'] = 'none';

  // Check for hostname verification failure
  if (received.toLowerCase().includes('hostname verification failed')) {
    warnings.push('Hostname verification failed');
    suspiciousLevel = 'high';
  }

  // Check for certificate verification issues
  if (hop.certificate?.verificationStatus && 
      hop.certificate.verificationStatus.toLowerCase().includes('fail')) {
    warnings.push('Certificate verification failed');
    suspiciousLevel = 'high';
  }

  // Check for missing TLS
  if (hop.protocol && !hop.tls && !hop.protocol.includes('HTTPS')) {
    warnings.push('No encryption detected');
    suspiciousLevel = suspiciousLevel === 'high' ? 'high' : 'medium';
  }

  // Check TLS version
  if (hop.tls) {
    const version = hop.tls.version.toLowerCase();
    if (version.includes('1.0') || version.includes('1_0')) {
      warnings.push('Using deprecated TLS 1.0');
      suspiciousLevel = suspiciousLevel === 'high' ? 'high' : 'medium';
    } else if (version.includes('1.1') || version.includes('1_1')) {
      warnings.push('Using deprecated TLS 1.1');
      suspiciousLevel = suspiciousLevel === 'high' ? 'high' : 'medium';
    } else if (version.includes('1.2') || version.includes('1_2')) {
      warnings.push('Using TLS 1.2 (consider upgrading to TLS 1.3)');
      suspiciousLevel = suspiciousLevel === 'none' ? 'low' : suspiciousLevel;
    }

    // Check cipher strength
    const cipherStrength = getCipherStrength(hop.tls.cipher);
    if (cipherStrength === 'deprecated') {
      warnings.push(`Using deprecated cipher: ${hop.tls.cipher}`);
      suspiciousLevel = 'high';
    } else if (cipherStrength === 'weak') {
      warnings.push(`Using weak cipher: ${hop.tls.cipher}`);
      suspiciousLevel = suspiciousLevel === 'high' ? 'high' : 'medium';
    }
  }

  // Check for suspicious delays
  if (isSuspiciousDelay(hop.delay)) {
    if (hop.delay && hop.delay < 0) {
      warnings.push(`Negative delay detected (${hop.delay}s) - clock skew or tampering`);
      suspiciousLevel = 'high';
    } else {
      warnings.push(`Unusually long delay (${Math.round(hop.delay!)}s)`);
      suspiciousLevel = suspiciousLevel === 'high' ? 'high' : 'medium';
    }
  }

  // Check for private IPs in public path
  if (isPrivateIP(hop.fromIp) && hop.hop > 1) {
    warnings.push(`Private IP address in relay path: ${hop.fromIp}`);
    suspiciousLevel = suspiciousLevel === 'high' ? 'high' : 'low';
  }

  // Assign warnings if any were found
  if (warnings.length > 0) {
    hop.warnings = warnings;
    hop.suspiciousLevel = suspiciousLevel;
  } else {
    hop.suspiciousLevel = 'none';
  }
};

/**
 * Parse Received headers to build relay path
 */
const parseReceivedHeaders = (headers: Map<string, string[]>): RelayHop[] => {
  const receivedHeaders = headers.get('received') || [];
  const hops: RelayHop[] = [];

  receivedHeaders.forEach((received, index) => {
    const hop: RelayHop = {
      hop: receivedHeaders.length - index, // Reverse order (first received is last hop)
      from: '',
      by: '',
    };

    // Parse "from" server
    const fromMatch = received.match(/from\s+([^\s(]+)(?:\s+\(([^)]+)\))?/i);
    if (fromMatch) {
      hop.from = fromMatch[1];
      if (fromMatch[2]) {
        const ipMatch = fromMatch[2].match(/(\d+\.\d+\.\d+\.\d+|[0-9a-f:]+)/i);
        if (ipMatch) {
          hop.fromIp = ipMatch[1];
        }
      }
    }

    // Parse "by" server
    const byMatch = received.match(/by\s+([^\s(]+)(?:\s+\(([^)]+)\))?/i);
    if (byMatch) {
      hop.by = byMatch[1];
      if (byMatch[2]) {
        const ipMatch = byMatch[2].match(/(\d+\.\d+\.\d+\.\d+|[0-9a-f:]+)/i);
        if (ipMatch) {
          hop.byIp = ipMatch[1];
        }
      }
    }

    // Parse protocol
    const withMatch = received.match(/with\s+([^\s;]+)/i);
    if (withMatch) {
      hop.protocol = withMatch[1];
    }

    // Parse timestamp
    const dateMatch = received.match(/;\s*(.+)$/);
    if (dateMatch) {
      hop.timestamp = dateMatch[1].trim();
    }

    // Extract TLS information
    hop.tls = extractTLSInfo(received);

    // Extract certificate information
    hop.certificate = extractCertificateInfo(received);

    // Extract queue ID
    hop.queueId = extractQueueId(received);

    // Extract recipient "for" address
    hop.forAddress = extractForAddress(received);

    // Extract "via" information
    hop.via = extractViaInfo(received);

    hops.push(hop);
  });

  // Calculate delays between hops
  for (let i = 0; i < hops.length - 1; i++) {
    if (hops[i].timestamp && hops[i + 1].timestamp) {
      try {
        const date1 = new Date(hops[i].timestamp!);
        const date2 = new Date(hops[i + 1].timestamp!);
        hops[i].delay = (date2.getTime() - date1.getTime()) / 1000; // Keep actual sign for suspicious detection
      } catch {
        // Invalid date format
      }
    }
  }

  // Detect suspicious patterns in each hop
  receivedHeaders.forEach((received, index) => {
    detectSuspiciousHop(hops[index], received);
  });

  return hops.reverse(); // Return in chronological order
};

/**
 * Calculate total delivery timing
 */
const calculateDeliveryTiming = (relayPath: RelayHop[]): ParsedEmailHeaders['timing'] => {
  if (relayPath.length === 0) {
    return { totalDelay: 0 };
  }

  const firstHop = relayPath[0];
  const lastHop = relayPath[relayPath.length - 1];

  if (!firstHop.timestamp || !lastHop.timestamp) {
    return { totalDelay: 0 };
  }

  try {
    const firstDate = new Date(firstHop.timestamp);
    const lastDate = new Date(lastHop.timestamp);
    const totalDelay = (lastDate.getTime() - firstDate.getTime()) / 1000;

    return {
      totalDelay: Math.max(0, totalDelay),
      firstReceived: firstDate,
      lastReceived: lastDate,
    };
  } catch {
    return { totalDelay: 0 };
  }
};

/**
 * Detect potential security issues
 */
const detectSecurityIssues = (
  headers: Map<string, string[]>,
  authentication: ParsedEmailHeaders['authentication']
): string[] => {
  const issues: string[] = [];

  // Check authentication failures
  if (authentication.spf.status === 'fail') {
    issues.push('SPF authentication failed - sender may be spoofed');
  }

  if (authentication.dkim.status === 'fail') {
    issues.push('DKIM signature verification failed - message may have been tampered with');
  }

  if (authentication.dmarc.status === 'fail') {
    issues.push('DMARC validation failed - message does not comply with domain policy');
  }

  // Check for mismatched domains
  const from = headers.get('from')?.[0] || '';
  const returnPath = headers.get('return-path')?.[0] || '';
  
  const fromDomain = from.match(/@([^\s>]+)/)?.[1];
  const returnDomain = returnPath.match(/@([^\s>]+)/)?.[1];
  
  if (fromDomain && returnDomain && fromDomain !== returnDomain) {
    issues.push(`From domain (${fromDomain}) differs from Return-Path domain (${returnDomain})`);
  }

  return issues;
};

/**
 * Get an example email header for testing
 */
export const getExampleEmailHeader = (): string => {
  return `Received: from MX01PR01MB1234.region01.prod.example.com (2001:db8:85a3::8a2e:370:7334)
 by MX02PR01MB5678.region01.prod.example.com with HTTPS; Thu, 16 Oct 2025
 11:27:28 +0000
Authentication-Results: spf=pass (sender IP is 198.51.100.42)
 smtp.mailfrom=examplehost.com; dkim=pass (signature was verified)
 header.d=examplehost.com;dmarc=pass action=none header.from=examplehost.com;compauth=pass
 reason=100
Received-SPF: Pass (protection.example.com: domain of examplehost.com designates
 198.51.100.42 as permitted sender) receiver=protection.example.com;
 client-ip=198.51.100.42; helo=smtp-relay-01.mail.examplehost.com; pr=C
Received: from smtp-relay-01.mail.examplehost.com (198.51.100.42) by
 MX03FILTER01.mail.protection.example.com (10.0.1.50) with Microsoft
 SMTP Server (version=TLS1_3, cipher=TLS_AES_256_GCM_SHA384) id 15.20.9228.7
 via Frontend Transport; Thu, 16 Oct 2025 11:27:26 +0000
DKIM-Signature: v=1; a=rsa-sha256; c=relaxed/relaxed; t=1760614046;
    s=example-dkim-0002; d=examplehost.com;
    h=Subject:From:To:Date:Message-ID:Cc:Date:From:Subject:Sender;
    bh=AbCdEfGhIjKlMnOpQrStUvWxYz0123456789ABCDEFGHIJK=;
    b=a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0u1v2w3x4y5z6A7B8C9D0E1F2G3
Received: from mail-server-01.internal.examplehost.com ([203.0.113.15])
    by smtp-outbound.mail.examplehost.com (RZmta 53.4.2 OK)
    with ESMTPSA id ABC123XYZ456DEF
    for <john.smith@example.com>;
    Thu, 16 Oct 2025 13:27:26 +0200 (CEST)
Received: from mail-server-01.datacenter.examplehost.com (internal-relay-10.datacenter.examplehost.com [172.16.0.10])
	by mail-server-01.internal.examplehost.com (Postfix) with ESMTPSA id 1A2B3C400123
	for <john.smith@example.com>; Thu, 16 Oct 2025 13:27:26 +0200 (CEST)
Date: Thu, 16 Oct 2025 13:27:26 +0200
To: john.smith@example.com
From: Billing Department <billing@examplehost.com>
Subject: Your Invoice for Account Number 12345678
Message-ID: <ABC123XYZ456DEF.RZmta@smtp-outbound.mail.examplehost.com>
Return-Path: billing-bounce+1A2B3C4D5E6F7G8H9I0J1K2L3M4N5O6P@examplehost.com
Content-Type: text/plain; charset="UTF-8"`;
};

